import { OrganisationsSearchOptionsQuery } from '@wwtr/generated';
import React, { memo, useState } from 'react';
import { Tab, Transition } from '@headlessui/react';
import { Badge, Button, Pagination, Skeleton } from '@wwtr/components/ui';
import { AdjustmentsIcon, ArrowLeftIcon } from '@heroicons/react/solid';
import { OrganizationListingList } from '../OrganizationListingList';
import { useSearchFilters } from '@wwtr/libs/contexts';
import cn from 'classnames';
import { FacilitiesListingList } from '../FacilitiesListingList';
import { useRouter } from 'next/router';
import { OrganizationListingFilters } from '../OrganizationListingFilters';
import { SearchLocationWithOrganizationEntry } from '../OrganizationListing/OrganizationListingContainer';
import { useBreakpoints } from '@wwtr/libs/hooks';

export type SearchListingSidebarProps = {
  totalOrganizations: number;
  totalFacilities: number;
  organizationsResults: SearchLocationWithOrganizationEntry[];
  facilitiesResults: SearchLocationWithOrganizationEntry[];
  isLoadingResults: boolean;
  isLoadingFilterData: boolean;
  filterData: OrganisationsSearchOptionsQuery;
  onHover: (entryId: string) => void;
  selectedTab: string;
  onTabChange: (selectedTab: SidebarTab) => void;
  pageNumber: number;
};

const getAmount = (amount: number): string =>
  amount ? (amount > 100 ? '100+' : amount.toString()) : null;

export type SidebarTab = {
  id: 'organizations' | 'facilities';
  title: string;
  path: string;
};

const tabGroups: SidebarTab[] = [
  { id: 'organizations', title: 'Organisaties', path: 'organisaties' },
  { id: 'facilities', title: 'Hulpaanbod', path: 'aanbod' },
];

const PAGE_SIZE = 20;

export const SearchListingSidebar = memo(
  ({
    organizationsResults,
    facilitiesResults,
    isLoadingResults,
    isLoadingFilterData,
    filterData,
    onHover,
    selectedTab,
    onTabChange,
    totalOrganizations,
    totalFacilities,
    pageNumber,
  }: SearchListingSidebarProps) => {
    const router = useRouter();
    const [hasFiltersOpen, setHasFiltersOpen] = useState(false);
    const { filters } = useSearchFilters();
    const { isMobile } = useBreakpoints();

    const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
      const newQuery = {
        ...router.query,
        params: [router.query.params[0], value.toString()],
      };

      router.push({ pathname: router.pathname, query: newQuery }, null, {
        shallow: true,
        scroll: true,
      });
    };

    const openFilterPanel = () => setHasFiltersOpen(true);
    const closeFilterPanel = () => setHasFiltersOpen(false);

    const convertIndexToTabName = (index: number) => {
      onTabChange(tabGroups[index]);
    };

    if (!router.isReady) {
      return null;
    } else {
      const selectedIndex = selectedTab === 'organisaties' ? 0 : 1;
      return (
        <div className="relative min-h-screen">
          <Transition
            show={!hasFiltersOpen}
            enter="transition duration-200"
            enterFrom="opacity-0 -translate-x-full"
            enterTo="opacity-100 translate-x-0"
            leave="transition duration-200"
            leaveFrom="opacity-100 translate-x-0"
            leaveTo="opacity-0 -translate-x-full"
            className="transform"
          >
            <div className="border-b border-gray-100">
              {isLoadingResults ? (
                <div className="px-8 py-5 text-gray-700 text-sm flex flex-row items-end justify-end">
                  <Skeleton className="w-24 h-8 rounded-full mr-4" />
                  <Skeleton className="w-36 h-8 rounded-full" />
                </div>
              ) : (
                <>
                  <div className="flex flex-row px-8 py-4 text-gray-700 text-sm border-gray-100 items-center justify-end">
                    <Button
                      size="sm"
                      color="powerblue"
                      appearance="outline"
                      withIcon
                      onClick={openFilterPanel}
                    >
                      <AdjustmentsIcon className="w-5 h-5 transform rotate-90" />
                      <span>Filters</span>
                      {filters.size > 0 && (
                        <div className="px-2 rounded-full text-xs bg-hotpink text-white">
                          {filters.size}
                        </div>
                      )}
                    </Button>
                  </div>
                  {/*<div className="flex px-8 py-4 text-gray-700 text-sm justify-between">
                  <FormInput></FormInput>
                </div>*/}
                </>
              )}
            </div>
            <div className="flex-1">
              <Tab.Group
                selectedIndex={selectedIndex}
                onChange={convertIndexToTabName}
              >
                <Tab.List className="flex">
                  {tabGroups.map(tab => (
                    <Tab
                      key={`results_organizations_${tab.id}`}
                      className={({ selected }) =>
                        cn(
                          'w-full py-4 font-semibold border-b-2 border-white',
                          'focus:outline-none',
                          selected
                            ? 'bg-white'
                            : 'bg-gradient-to-b from-gray-100 to-gray-200 text-gray-400 hover:text-gray-600'
                        )
                      }
                    >
                      {({ selected }) =>
                        tab.id === 'organizations' ? (
                          <span>
                            {tab.title}
                            {totalOrganizations > 0 && (
                              <Badge
                                className={cn(
                                  'bg-powerblue text-white text-xs ml-2',
                                  selected
                                    ? 'bg-powerblue text-white'
                                    : 'bg-gray-400 text-gray-700'
                                )}
                              >
                                {getAmount(totalOrganizations)}
                              </Badge>
                            )}
                          </span>
                        ) : (
                          <span>
                            {tab.title}
                            {totalFacilities > 0 && (
                              <Badge
                                className={cn(
                                  'bg-powerblue text-white text-xs ml-2',
                                  selected
                                    ? 'bg-powerblue text-white'
                                    : 'bg-gray-400 text-gray-700'
                                )}
                              >
                                {getAmount(totalFacilities)}
                              </Badge>
                            )}
                          </span>
                        )
                      }
                    </Tab>
                  ))}
                </Tab.List>
                <Tab.Panels>
                  <Tab.Panel
                    key={'tab_panel_results_organizations'}
                    className={cn(
                      'focus:outline-none focus:ring-2 ring-offset-2 ring-offset-blue-400 ring-white ring-opacity-60'
                    )}
                  >
                    <div className="flex-1 px-8 py-4 text-gray-700 text-sm border-b border-gray-100 items-center justify-between">
                      {isLoadingResults ? (
                        <Skeleton className="w-72 h-3 my-1" />
                      ) : totalOrganizations > 100 ? (
                        <p>Meer dan 100 gevonden organisaties</p>
                      ) : (
                        <p>{totalOrganizations} gevonden organisaties</p>
                      )}
                    </div>
                    <OrganizationListingList
                      entries={organizationsResults}
                      isLoading={isLoadingResults}
                      onHover={onHover}
                    />
                    {totalOrganizations > PAGE_SIZE && (
                      <Pagination
                        count={Math.ceil(totalOrganizations / PAGE_SIZE)}
                        page={pageNumber}
                        boundaryCount={0}
                        onChange={handleChange}
                        isLoading={isLoadingResults}
                        hidePrevButton={isMobile}
                      />
                    )}
                  </Tab.Panel>

                  <Tab.Panel
                    key={'tab_panel_results_facilities'}
                    className={cn(
                      'focus:outline-none focus:ring-2 ring-offset-2 ring-offset-blue-400 ring-white ring-opacity-60'
                    )}
                  >
                    <div className="flex-1 px-8 py-4 text-gray-700 text-sm border-b border-gray-100 items-center justify-between">
                      {isLoadingResults ? (
                        <Skeleton className="w-72 h-3 my-1" />
                      ) : totalFacilities > 100 ? (
                        <p>
                          Meer dan 100 resultaten gevonden in het hulpaanbod
                        </p>
                      ) : (
                        <p>
                          {totalFacilities} resultaten gevonden in het
                          hulpaanbod
                        </p>
                      )}
                    </div>
                    <FacilitiesListingList
                      entries={facilitiesResults}
                      isLoading={isLoadingResults}
                      onHover={onHover}
                    />
                    {totalFacilities > PAGE_SIZE && (
                      <Pagination
                        count={Math.ceil(totalFacilities / PAGE_SIZE)}
                        page={pageNumber}
                        boundaryCount={0}
                        onChange={handleChange}
                        isLoading={isLoadingResults}
                        hidePrevButton={isMobile}
                      />
                    )}
                  </Tab.Panel>
                </Tab.Panels>
              </Tab.Group>
            </div>
          </Transition>
          <Transition
            show={hasFiltersOpen}
            enter="transition duration-200"
            enterFrom="opacity-0 translate-x-full"
            enterTo="opacity-100 translate-x-0"
            leave="transition duration-200"
            leaveFrom="opacity-100 translate-x-0"
            leaveTo="opacity-0 translate-x-full"
            className="absolute top-0 left-0 right-0 bottom-0 transform"
          >
            <div className="border-b border-gray-100">
              <div className="flex px-8 py-4 text-gray-700 text-sm justify-end">
                <Button
                  size="sm"
                  color="powerblue"
                  appearance="outline"
                  withIcon
                  onClick={closeFilterPanel}
                >
                  <ArrowLeftIcon className="w-5 h-5" />
                  <span>Terug naar resultaten</span>
                </Button>
              </div>
            </div>
            <OrganizationListingFilters
              data={filterData}
              isLoading={isLoadingFilterData}
              onSubmit={closeFilterPanel}
            />
          </Transition>
        </div>
      );
    }
  }
);

SearchListingSidebar.displayName = 'SearchListingSidebar';
